/**
 * Settings page — grouped sections with inline editing, save per section.
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchSettings, bulkUpsertSettings, fetchEmailDiagnostics, fetchTrainingNotes, createTrainingNote, updateTrainingNote, deleteTrainingNote } from '../api/settings';
import { getAiSettings, updateAiSettings, triggerMonitor } from '../api/agent';
import { LoadingSpinner } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { useSystemMode } from '../context/SystemModeContext';
import { resetTestDatabase } from '../api/systemMode';

/* ── Setting key definitions ── */

const SECTIONS = [
  {
    id: 'shop',
    title: 'Shop Capacity',
    description: 'Configure builder count and working hours for the scheduling engine.',
    icon: WrenchIcon,
    fields: [
      { key: 'shop_capacity_builders', label: 'Number of Builders', type: 'number', default: '2', help: 'Simultaneous builders working in the shop.' },
      { key: 'shop_hours_per_day', label: 'Hours Per Day', type: 'number', default: '8', help: 'Working hours available per day per builder.' },
      { key: 'shop_days_per_week', label: 'Days Per Week', type: 'number', default: '5', help: 'Working days per week (5 = weekdays only).' },
      { key: 'work_weekdays_only', label: 'Weekdays Only', type: 'boolean', default: 'true', help: 'Skip Saturday and Sunday in scheduling.' },
    ],
  },
  {
    id: 'scheduling',
    title: 'Scheduling',
    description: 'Control scheduling horizon and auto-pause behavior.',
    icon: CalendarIcon,
    fields: [
      { key: 'scheduling_horizon_days', label: 'Horizon (days)', type: 'number', default: '90', help: 'How far ahead the scheduler projects dates.' },
      { key: 'build_paused_threshold_days', label: 'Build Paused Threshold (days)', type: 'number', default: '2', help: 'Days with no clock-in before a job is auto-paused.' },
    ],
  },
  {
    id: 'email',
    title: 'Email Integration',
    description: 'Gmail polling and RFQ email configuration.',
    icon: EnvelopeIcon,
    fields: [
      { key: 'email_poll_interval_minutes', label: 'Poll Interval (minutes)', type: 'number', default: '5', help: 'How often to check Gmail for new messages.' },
      { key: 'email_from_address', label: 'From Address', type: 'text', default: '', help: 'Email address used as the sender for RFQs and POs.' },
      { key: 'email_reply_to', label: 'Reply-To Address', type: 'text', default: '', help: 'Reply-to address for outbound emails (optional).' },
    ],
    hasDiagnostics: true,
  },
  {
    id: 'pricing',
    title: 'Pricing Thresholds',
    description: 'Define when quote prices are considered fresh, aging, or stale.',
    icon: TagIcon,
    fields: [
      { key: 'pricing_fresh_days', label: 'Fresh (days)', type: 'number', default: '30', help: 'Quotes within this age are considered fresh (green).' },
      { key: 'pricing_stale_days', label: 'Stale (days)', type: 'number', default: '90', help: 'Quotes older than this are stale (red). Between fresh and stale is aging (yellow).' },
    ],
  },
  {
    id: 'sequences',
    title: 'Number Sequences',
    description: 'Starting numbers for auto-generated job and quote numbers.',
    icon: HashIcon,
    fields: [
      { key: 'next_job_number', label: 'Next Job Number', type: 'number', default: '2000', help: 'Next auto-assigned job number.' },
      { key: 'next_quote_number', label: 'Next Quote Number', type: 'number', default: '1000', help: 'Next auto-assigned customer quote number.' },
    ],
  },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.is_staff || user?.role === 'admin';
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});       // sectionId → { key: newValue }
  const [saving, setSaving] = useState(null);     // sectionId currently saving
  const [saved, setSaved] = useState(null);       // sectionId just saved
  const [diagnostics, setDiagnostics] = useState(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const all = await fetchSettings();
      const map = {};
      (Array.isArray(all) ? all : []).forEach((s) => { map[s.key] = s.value; });
      setSettings(map);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Load email diagnostics
  useEffect(() => {
    fetchEmailDiagnostics().then(setDiagnostics).catch(() => {});
  }, []);

  function getValue(key, defaultVal) {
    // Check dirty edits first, then saved settings, then default
    for (const sec of Object.values(dirty)) {
      if (key in sec) return sec[key];
    }
    if (key in settings) return settings[key];
    return defaultVal;
  }

  function handleChange(sectionId, key, value) {
    setDirty((prev) => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || {}), [key]: value },
    }));
    setSaved(null);
  }

  function isSectionDirty(sectionId) {
    return dirty[sectionId] && Object.keys(dirty[sectionId]).length > 0;
  }

  async function handleSave(sectionId) {
    const sectionDirty = dirty[sectionId];
    if (!sectionDirty || Object.keys(sectionDirty).length === 0) return;

    setSaving(sectionId);
    try {
      await bulkUpsertSettings(sectionDirty);
      // Merge into settings
      setSettings((prev) => ({ ...prev, ...sectionDirty }));
      // Clear dirty for this section
      setDirty((prev) => { const next = { ...prev }; delete next[sectionId]; return next; });
      setSaved(sectionId);
      setTimeout(() => setSaved((s) => s === sectionId ? null : s), 2000);
    } catch { /* ignore */ }
    finally { setSaving(null); }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-lg font-semibold text-foreground">Settings</h1>

      {isAdmin && <TestModeSection />}
      {isAdmin && <AiSettingsSection />}
      {isAdmin && <TrainingNotesSection />}

      {SECTIONS.map((section) => {
        const Icon = section.icon;
        return (
          <div key={section.id} className="bg-white rounded-lg border border-gray-200">
            {/* Section header */}
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-gray-400" />
                <h2 className="text-sm font-semibold text-foreground">{section.title}</h2>
              </div>
              <p className="text-xs text-gray-500 mt-1">{section.description}</p>
            </div>

            {/* Fields */}
            <div className="px-5 py-4 space-y-4">
              {section.fields.map((field) => {
                const val = getValue(field.key, field.default);
                return (
                  <div key={field.key} className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <label className="text-sm font-medium text-gray-700">{field.label}</label>
                      {field.help && <p className="text-xs text-gray-400 mt-0.5">{field.help}</p>}
                    </div>
                    <div className="w-48 flex-shrink-0">
                      {field.type === 'boolean' ? (
                        <button
                          type="button"
                          onClick={() => {
                            const current = val === 'true' || val === true;
                            handleChange(section.id, field.key, String(!current));
                          }}
                          className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
                            val === 'true' || val === true ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
                              val === 'true' || val === true ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      ) : field.type === 'number' ? (
                        <input
                          type="number"
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm text-right"
                          value={val}
                          onChange={(e) => handleChange(section.id, field.key, e.target.value)}
                        />
                      ) : (
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                          value={val}
                          onChange={(e) => handleChange(section.id, field.key, e.target.value)}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Email diagnostics */}
            {section.hasDiagnostics && (
              <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Diagnostics</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <DiagCard
                    label="Connection"
                    value={settings.gmail_oauth_token ? 'Connected' : 'Not Connected'}
                    color={settings.gmail_oauth_token ? 'green' : 'red'}
                  />
                  <DiagCard
                    label="Last Poll"
                    value={settings.email_last_poll_at
                      ? new Date(settings.email_last_poll_at).toLocaleString()
                      : 'Never'}
                    color="gray"
                  />
                  <DiagCard
                    label="Unmatched Emails"
                    value={diagnostics?.unmatched_count ?? '—'}
                    color={diagnostics?.unmatched_count > 0 ? 'yellow' : 'gray'}
                  />
                  <DiagCard
                    label="Parse Errors"
                    value={settings.email_parse_error_count || '0'}
                    color={parseInt(settings.email_parse_error_count || '0') > 0 ? 'red' : 'gray'}
                  />
                </div>
              </div>
            )}

            {/* Save button */}
            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
              {saved === section.id && (
                <span className="text-xs text-green-600 font-medium">Saved</span>
              )}
              <button
                onClick={() => handleSave(section.id)}
                disabled={!isSectionDirty(section.id) || saving === section.id}
                className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                  isSectionDirty(section.id)
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {saving === section.id ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Test Mode Section (admin only) ── */

function TestModeSection() {
  const { isTestMode, toggleMode, test_mode_enabled_at } = useSystemMode();
  const [toggling, setToggling] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  async function handleToggle() {
    const confirmMsg = isTestMode
      ? 'Switch back to LIVE mode (Pump Source)?\n\nThis will use live production data.'
      : 'Switch to TEST mode (JobLogic)?\n\nThis will use a separate test database. No changes will affect live data.';

    if (!window.confirm(confirmMsg)) return;

    setToggling(true);
    try {
      await toggleMode();
      // Page will reload after toggle
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to toggle mode');
      setToggling(false);
    }
  }

  async function handleReset() {
    if (!window.confirm(
      '⚠️ RESET TEST DATABASE\n\n' +
      'This will DELETE ALL DATA in the test database and start fresh.\n\n' +
      'This cannot be undone. Continue?'
    )) return;

    setResetting(true);
    setResetResult(null);
    try {
      const result = await resetTestDatabase();
      setResetResult(result.message);
    } catch (err) {
      setResetResult(err?.response?.data?.error || 'Reset failed');
    } finally {
      setResetting(false);
    }
  }

  return (
    <div className={`rounded-lg border ${isTestMode ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200 bg-white'}`}>
      {/* Header */}
      <div className={`px-5 py-4 border-b rounded-t-lg ${isTestMode ? 'border-blue-200 bg-blue-100' : 'border-gray-100 bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <ComputerIcon className={`w-5 h-5 ${isTestMode ? 'text-blue-600' : 'text-gray-400'}`} />
          <h2 className={`text-sm font-semibold ${isTestMode ? 'text-blue-900' : 'text-gray-900'}`}>
            {isTestMode ? 'JobLogic (Test Mode)' : 'Test Mode'}
          </h2>
          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
            isTestMode ? 'bg-blue-200 text-blue-700' : 'bg-gray-200 text-gray-600'
          }`}>
            Admin Only
          </span>
          {isTestMode && (
            <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full font-medium animate-pulse">
              ACTIVE
            </span>
          )}
        </div>
        <p className={`text-xs mt-1 ${isTestMode ? 'text-blue-700' : 'text-gray-500'}`}>
          {isTestMode
            ? 'You are currently using the test database. All operations are isolated from live data.'
            : 'Switch to a separate test database to safely test features without affecting live data.'}
        </p>
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Toggle */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700">Test Mode</label>
            <p className="text-xs text-gray-400 mt-0.5">
              When enabled, all operations use a separate test database branded as "JobLogic".
              Your login credentials work in both modes.
            </p>
            {isTestMode && test_mode_enabled_at && (
              <p className="text-xs text-blue-600 mt-1 font-medium">
                Enabled since {new Date(test_mode_enabled_at).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={handleToggle}
              disabled={toggling}
              className={`relative inline-flex h-6 w-11 rounded-full transition-colors disabled:opacity-50 ${
                isTestMode ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
                  isTestMode ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Reset button (only when NOT in test mode) */}
        {!isTestMode && (
          <div className="flex items-start justify-between gap-4 pt-2 border-t border-gray-100">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700">Reset Test Database</label>
              <p className="text-xs text-gray-400 mt-0.5">
                Clear all test data and start fresh. Only available when in live mode.
              </p>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={handleReset}
                disabled={resetting}
                className="px-3 py-1.5 text-xs border border-red-300 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                {resetting ? 'Resetting…' : 'Reset Test DB'}
              </button>
            </div>
          </div>
        )}

        {resetResult && (
          <div className="text-xs text-gray-600 bg-gray-50 rounded px-3 py-2">
            {resetResult}
          </div>
        )}
      </div>
    </div>
  );
}

function ComputerIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
    </svg>
  );
}

/* ── AI Settings Section (admin only) ── */

function AiSettingsSection() {
  const [aiSettings, setAiSettings] = useState({
    ai_write_access: 'false',
    ai_monitoring_enabled: 'true',
    ai_polling_interval: '10',
  });
  const [serverSettings, setServerSettings] = useState(null); // tracks what's saved on server
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);

  useEffect(() => {
    getAiSettings().then((data) => {
      setAiSettings(data);
      setServerSettings(data);
    }).catch(() => {});
  }, []);

  // Check if any setting differs from what's on the server
  const isDirty = serverSettings && (
    aiSettings.ai_write_access !== serverSettings.ai_write_access ||
    aiSettings.ai_monitoring_enabled !== serverSettings.ai_monitoring_enabled ||
    aiSettings.ai_polling_interval !== serverSettings.ai_polling_interval
  );

  function toggle(key) {
    setAiSettings((prev) => ({
      ...prev,
      [key]: prev[key] === 'true' ? 'false' : 'true',
    }));
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateAiSettings(aiSettings);
      setServerSettings({ ...aiSettings }); // update server snapshot
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save AI settings');
    } finally { setSaving(false); }
  }

  async function handleTrigger() {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const result = await triggerMonitor();
      setTriggerResult(`Monitor ran — ${result.notifications_created} new notification(s).`);
    } catch {
      setTriggerResult('Monitor trigger failed.');
    } finally {
      setTriggering(false);
    }
  }

  const isWriteOn = aiSettings.ai_write_access === 'true';
  const isMonitorOn = aiSettings.ai_monitoring_enabled === 'true';

  return (
    <div className="bg-white rounded-lg border border-blue-200 ring-1 ring-blue-100">
      {/* Header */}
      <div className="px-5 py-4 border-b border-blue-100 bg-blue-50 rounded-t-lg">
        <div className="flex items-center gap-2">
          <SparklesIconSmall className="w-5 h-5 text-blue-500" />
          <h2 className="text-sm font-semibold text-blue-900">AI Agent</h2>
          <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-medium">Admin Only</span>
        </div>
        <p className="text-xs text-blue-600 mt-1">Control Claude AI assistant behavior and write permissions.</p>
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Write access toggle */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700">Write Access</label>
            <p className="text-xs text-gray-400 mt-0.5">
              Allow Claude to execute actions (send emails, update records, mark received) when you confirm them.
              When off, Claude proposes actions but cannot execute them.
            </p>
            {isWriteOn && (
              <p className="text-xs text-amber-600 mt-1 font-medium">
                ⚠ Write access is ON — Claude can modify records when you confirm.
              </p>
            )}
          </div>
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={() => toggle('ai_write_access')}
              className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
                isWriteOn ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
                  isWriteOn ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Monitoring toggle */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700">Proactive Monitoring</label>
            <p className="text-xs text-gray-400 mt-0.5">
              Claude scans app state every {aiSettings.ai_polling_interval} minutes during business hours
              (Mon–Fri 7am–5pm) and surfaces issues to the notification bell.
            </p>
          </div>
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={() => toggle('ai_monitoring_enabled')}
              className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
                isMonitorOn ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
                  isMonitorOn ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Polling interval */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700">Polling Interval (minutes)</label>
            <p className="text-xs text-gray-400 mt-0.5">How often Claude checks app state during business hours.</p>
          </div>
          <div className="w-24 flex-shrink-0">
            <input
              type="number"
              min="5"
              max="60"
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm text-right"
              value={aiSettings.ai_polling_interval}
              onChange={(e) => {
                setAiSettings((prev) => ({ ...prev, ai_polling_interval: e.target.value }));
                setSaved(false);
              }}
            />
          </div>
        </div>
      </div>

      {/* Footer: save + manual trigger */}
      <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={handleTrigger}
            disabled={triggering}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {triggering ? 'Running…' : 'Run Monitor Now'}
          </button>
          {triggerResult && (
            <span className="text-xs text-gray-500">{triggerResult}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {saved && (
            <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Saved
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
              isDirty
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SparklesIconSmall({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
    </svg>
  );
}

/* ── Training Notes Section (admin only) ── */

function TrainingNotesSection() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // note ID being edited
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    note_text: '',
    applies_to_vendor_name: '',
    applies_to_email_type: '',
    applies_to_sender_pattern: '',
    is_active: true,
  });

  useEffect(() => {
    loadNotes();
  }, []);

  async function loadNotes() {
    setLoading(true);
    try {
      const data = await fetchTrainingNotes();
      setNotes(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }

  function resetForm() {
    setForm({
      note_text: '',
      applies_to_vendor_name: '',
      applies_to_email_type: '',
      applies_to_sender_pattern: '',
      is_active: true,
    });
    setEditing(null);
    setShowForm(false);
  }

  function startEdit(note) {
    setForm({
      note_text: note.note_text || '',
      applies_to_vendor_name: note.applies_to_vendor_name || '',
      applies_to_email_type: note.applies_to_email_type || '',
      applies_to_sender_pattern: note.applies_to_sender_pattern || '',
      is_active: note.is_active !== false,
    });
    setEditing(note.id);
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.note_text.trim()) {
      alert('Note text is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateTrainingNote(editing, form);
      } else {
        await createTrainingNote(form);
      }
      await loadNotes();
      resetForm();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this training note?')) return;
    try {
      await deleteTrainingNote(id);
      await loadNotes();
    } catch {
      alert('Failed to delete');
    }
  }

  return (
    <div className="bg-white rounded-lg border border-purple-200 ring-1 ring-purple-100">
      {/* Header */}
      <div className="px-5 py-4 border-b border-purple-100 bg-purple-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpenIcon className="w-5 h-5 text-purple-500" />
            <h2 className="text-sm font-semibold text-purple-900">AI Training Notes</h2>
            <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded font-medium">Admin Only</span>
          </div>
          {!showForm && (
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="px-3 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
            >
              + Add Note
            </button>
          )}
        </div>
        <p className="text-xs text-purple-600 mt-1">
          Custom instructions injected into AI email processing. Add vendor-specific rules without deployments.
        </p>
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Form */}
        {showForm && (
          <div className="border border-purple-200 rounded-lg p-4 bg-purple-25 space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Note Text *</label>
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                rows={5}
                value={form.note_text}
                onChange={(e) => setForm((f) => ({ ...f, note_text: e.target.value }))}
                placeholder="Enter training instructions for the AI..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Applies to Vendor (optional)</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  value={form.applies_to_vendor_name}
                  onChange={(e) => setForm((f) => ({ ...f, applies_to_vendor_name: e.target.value }))}
                  placeholder="e.g., BRUNET'S WELDING"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Sender Pattern (optional)</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  value={form.applies_to_sender_pattern}
                  onChange={(e) => setForm((f) => ({ ...f, applies_to_sender_pattern: e.target.value }))}
                  placeholder="e.g., @example.com"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="note-active"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="note-active" className="text-sm text-gray-700">Active</label>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving…' : editing ? 'Update' : 'Save'}
              </button>
              <button
                onClick={resetForm}
                className="px-4 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Notes list */}
        {loading ? (
          <div className="text-sm text-gray-500 py-4 text-center">Loading…</div>
        ) : notes.length === 0 ? (
          <div className="text-sm text-gray-500 py-4 text-center">
            No training notes yet. Click "+ Add Note" to create one.
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <div key={note.id} className={`border rounded-lg p-3 ${note.is_active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {note.applies_to_vendor_name && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                          Vendor: {note.applies_to_vendor_name}
                        </span>
                      )}
                      {note.applies_to_sender_pattern && (
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                          Sender: {note.applies_to_sender_pattern}
                        </span>
                      )}
                      {!note.is_active && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">Inactive</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.note_text}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => startEdit(note)}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Edit"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(note.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BookOpenIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

function PencilIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  );
}

function TrashIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  );
}

/* ── Diagnostic card ── */

function DiagCard({ label, value, color }) {
  const colors = {
    green: 'text-green-700 bg-green-50 border-green-200',
    red: 'text-red-700 bg-red-50 border-red-200',
    yellow: 'text-yellow-700 bg-yellow-50 border-yellow-200',
    gray: 'text-gray-600 bg-gray-50 border-gray-200',
  };
  return (
    <div className={`rounded-md border px-3 py-2 ${colors[color] || colors.gray}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-medium text-sm truncate">{value}</div>
    </div>
  );
}

/* ── Inline SVG icons ── */

function WrenchIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17l-5.59 5.59a2.121 2.121 0 01-3-3l5.59-5.59m0 0L21 3m-9.58 12.17L3 21m0 0h3v-3M21 3h-3v3" />
    </svg>
  );
}

function CalendarIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
    </svg>
  );
}

function EnvelopeIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  );
}

function TagIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
    </svg>
  );
}

function HashIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 8.25h15m-16.5 7.5h15m-1.8-13.5l-3.6 19.5m-2.1-19.5l-3.6 19.5" />
    </svg>
  );
}
